import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { isEmailVerified, sendVerificationEmail, verifyEmail } from "supertokens-web-js/recipe/emailverification";
import Session from "supertokens-web-js/recipe/session";

import { useLogout } from "@/authentication/useLogout";
import { AuthenticationPage } from "@/components/AuthenticationPage/AuthenticationPage";
import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { DEFAULT_APP_BRANDING } from "@/helpers/app-branding";
import { SUPPORT_EMAIL } from "@/helpers/constants";
import type { AccessTokenPayload } from "@/helpers/jwt";
import { sleep } from "@/helpers/sleep";
import { useBool } from "@/hooks/useBool";
import { useInterval } from "@/hooks/useInterval";
import { useIsWindowFocused } from "@/hooks/useIsWindowFocused";
import { getLocalStorageValue, updateLocalStorage } from "@/hooks/useLocalStorage";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useAccessTokenContext } from "@/providers/AccessTokenProvider";
import { usePublicAppBranding } from "@/providers/PublicAppBrandingProvider";
import { useConfigurationQueries } from "@/queries/configurations/queryOptions";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

import inboxPng from "../assets/inbox.png";

enum PageState {
  Initial,
  Expired,
  Sent,
  VerifiedDifferentDevice,
  Failed,
  SendFailed,
}

export function VerifyEmailPage(): React.ReactNode {
  const { getAccessTokenPayload } = useAccessTokenContext();
  const logout = useLogout();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const showFlashToast = useFlashToast();

  const initialState = location.state?.noLoader ? PageState.Sent : PageState.Initial;
  const [isSubmitting, submittingHandlers] = useBool();
  const [state, setState] = useState(initialState);
  const sessionChecksStopped = useRef(false);

  const fromSignUp = useQueryParam("from")[0] === "signup";
  const signUpOrigin = useQueryParam("sign-up-origin")[0];
  const [queryParamEmail] = useQueryParam("email");
  const [token] = useQueryParam("token");

  const configurationQueries = useConfigurationQueries();
  const { appBranding } = usePublicAppBranding();
  const {
    data: listAppBrandingConfigs = [],
    isPending: isPendingListAppBrandingConfigs,
    isError: isErrorListAppBrandingConfigs,
  } = useQuery(configurationQueries.listAppBrandings());

  const navigateToRegistration = useCallback(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get("code");
    const type = queryParams.get("type");

    const newQueryParams = new URLSearchParams();
    if (code) {
      newQueryParams.set("code", code);
    }

    if (type) {
      newQueryParams.set("type", type);
    }

    if (appBranding !== DEFAULT_APP_BRANDING) {
      newQueryParams.set("brand", appBranding);
    }

    void navigate(`${routes.registration.main()}?${newQueryParams.toString()}`);
  }, [navigate, appBranding]);

  const goToLogin = useCallback(
    (isCancelled?: () => boolean) => {
      sessionChecksStopped.current = true;
      if (!isCancelled?.()) {
        void navigate(routes.authentication.login());
      }
    },
    [navigate],
  );

  const redirectIfSessionMissing = useCallback(
    async (isCancelled?: () => boolean) => {
      // A failed session check is not proof that the user has signed out.
      const sessionExists = await Session.doesSessionExist().catch(() => true);
      if (sessionExists) {
        return false;
      }

      goToLogin(isCancelled);

      return true;
    },
    [goToLogin],
  );

  const sendVerifyEmail = useCallback(
    async (resend?: boolean) => {
      if (await redirectIfSessionMissing()) {
        return;
      }

      if (resend || !location.state?.noLoader) {
        submittingHandlers.setTrue();
      }

      // Wait for 1.5 seconds to make sure the user actually sees the spinner
      const sleepPromise = resend ? sleep(1500) : undefined;

      try {
        const response = await sendVerificationEmail({
          options: {
            preAPIHook: (x) => {
              const url = new URL(x.url);

              const ogSearchParams = new URLSearchParams(window.location.search);
              for (const [key, value] of ogSearchParams.entries()) {
                if (!url.searchParams.has(key)) {
                  url.searchParams.set(key, value);
                }
              }

              url.searchParams.set("languageId", i18n.language);
              url.searchParams.set("origin", "web");
              url.searchParams.set("brand", appBranding);

              return Promise.resolve({
                ...x,
                url: url.toString(),
              });
            },
          },
        });

        const userId = await Session.getUserId();

        updateLocalStorage(sentVerificationEmailStorageKey(userId), { date: Date.now() });

        if (response.status === "EMAIL_ALREADY_VERIFIED_ERROR") {
          // This can happen if the info about email verification in the session was outdated.
          navigateToRegistration();
        } else {
          // Make sure there's enough feedback for the user
          await sleepPromise;
          setState(PageState.Sent);

          if (resend) {
            showFlashToast({ variant: "success", message: t("page.verify-email.sent.resend-toast") });
          }
        }
      } catch (err: any) {
        if (await redirectIfSessionMissing()) {
          return;
        }

        console.error(err);
        showFlashToast({ variant: "alert", message: t("common.generic-error") });
        if (!resend) {
          setState(PageState.SendFailed);
        }
      } finally {
        submittingHandlers.setFalse();
      }
    },
    [
      location.state?.noLoader,
      submittingHandlers,
      i18n.language,
      appBranding,
      navigateToRegistration,
      showFlashToast,
      t,
      redirectIfSessionMissing,
    ],
  );

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        if (cancelled) {
          return;
        }

        // Sleep needed to make sure supertokens is initialized and can read the token...
        await sleep(200);
        if (cancelled) {
          return;
        }

        const status = await getEmailVerifiedStatus({
          token,
          queryParamEmail,
          getAccessTokenPayload,
        });
        if (cancelled) {
          return;
        }

        switch (status) {
          case VerifyEmailStatus.NoSession:
            // Redirect to login page
            goToLogin();
            break;
          case VerifyEmailStatus.Verified:
            navigateToRegistration();
            break;
          case VerifyEmailStatus.VerifiedDifferentDevice:
            setState(PageState.VerifiedDifferentDevice);
            break;
          case VerifyEmailStatus.Expired:
            setState(PageState.Expired);
            break;
          case VerifyEmailStatus.NotYetVerified: {
            // Instantly send email (on page load)
            const userId = await Session.getUserId();
            if (cancelled) {
              return;
            }

            const sentVerificationEmailState = getLocalStorageValue<{ date: number } | undefined>(
              sentVerificationEmailStorageKey(userId),
              undefined,
            );
            // If the email was sent less than 15 minutes ago, don't send another one
            const seconds = 15 * 60 * 1000;
            if (sentVerificationEmailState?.date && Date.now() - sentVerificationEmailState.date < seconds) {
              setState(PageState.Sent);

              return;
            }

            await sendVerifyEmail(false);
            break;
          }
          default:
            break;
        }
      } catch (err: any) {
        if (!token && (await redirectIfSessionMissing(() => cancelled))) {
          return;
        }

        console.error(err);
        if (!cancelled) {
          showFlashToast({ variant: "alert", message: t("common.generic-error") });

          setState(PageState.Failed);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    goToLogin,
    navigateToRegistration,
    sendVerifyEmail,
    queryParamEmail,
    token,
    location.search,
    showFlashToast,
    t,
    getAccessTokenPayload,
    redirectIfSessionMissing,
  ]);

  const isWindowFocused = useIsWindowFocused();
  const checkVerified = useCallback(
    async (isCancelled?: () => boolean) => {
      if (sessionChecksStopped.current) {
        return;
      }

      try {
        if (await redirectIfSessionMissing(isCancelled)) {
          return;
        }

        if ((await isEmailVerified()).isVerified && !isCancelled?.()) {
          navigateToRegistration();
        }
      } catch (err) {
        console.error(err);
        await redirectIfSessionMissing(isCancelled);
      }
    },
    [navigateToRegistration, redirectIfSessionMissing],
  );

  useInterval(() => {
    if (isWindowFocused && state === PageState.Sent) {
      void checkVerified();
    }
  }, 5000);

  useEffect(() => {
    let cancelled = false;

    function checkOnFocus() {
      if (!cancelled && state === PageState.Sent) {
        void checkVerified(() => cancelled);
      }
    }

    window.addEventListener("focus", checkOnFocus);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", checkOnFocus);
    };
  }, [checkVerified, state]);

  const showSteps = fromSignUp;
  let header = t("page.verify-email.title");
  if (state === PageState.Expired) {
    header = t("page.verify-email.expired.title");
  } else if (state === PageState.VerifiedDifferentDevice) {
    header = t("page.verify-email.outside-session.title");
  }

  let body: string | undefined;
  if (state === PageState.Expired) {
    body = t("page.verify-email.expired.description");
  } else if (state === PageState.VerifiedDifferentDevice) {
    body = t("page.verify-email.outside-session.description");
  } else if (state === PageState.SendFailed) {
    body = t("common.generic-error");
  }

  const appBrandingConfig = listAppBrandingConfigs?.find(
    (currAppBrandingConfig) => currAppBrandingConfig.id === appBranding,
  );

  if (isPendingListAppBrandingConfigs) {
    return null;
  }

  if (state === PageState.Failed) {
    return <ConnectionFailurePage poEditorCode={i18n.language} />;
  }

  if (isErrorListAppBrandingConfigs || !appBrandingConfig) {
    console.error("Error loading app branding configs or app branding config not found");

    return null;
  }

  return (
    <AuthenticationPage
      header={header}
      body={body}
      step={showSteps ? 2 : undefined}
      totalSteps={showSteps ? 2 : undefined}
    >
      {state === PageState.Initial ? <FullSizeLoader /> : null}
      {(state === PageState.Expired || state === PageState.SendFailed) && (
        <Stack gap="xl">
          <Button onClick={() => sendVerifyEmail(true)} fullWidth isLoading={isSubmitting}>
            {t("page.verify-email.resend")}
          </Button>
          <p className="text-caption">
            <Trans
              i18nKey="page.verify-email.expired.still-having-issues"
              components={{
                email: (content) => (
                  <Button variant="ghostPrimary" type="link" href={`mailto:${SUPPORT_EMAIL}`} isExternal>
                    {content}
                  </Button>
                ),
              }}
            />
          </p>
        </Stack>
      )}
      {state === PageState.VerifiedDifferentDevice &&
        (signUpOrigin === "web" ? (
          <Button variant="filled" type="link" href="/" fullWidth>
            {t("page.verify-email.outside-session.continue-in-page")}
          </Button>
        ) : (
          <Button variant="filled" type="link" href={`${appBrandingConfig?.deepLink}/sign-in`} isExternal fullWidth>
            {t("page.verify-email.outside-session.continue-in-app")}
          </Button>
        ))}
      {state === PageState.Sent && (
        <Stack gap="md">
          <img src={inboxPng} alt="Inbox" className="mx-auto" />
          <p className="mt-space-md flex flex-col">
            {t("page.verify-email.sent.description", { email: queryParamEmail || "" })}
            <strong className="mt-space-md">{t("page.verify-email.sent.no-link.title")}</strong>
            {t("page.verify-email.sent.no-link.description")}
          </p>
          <Stack gap="lg" align="center">
            <Button
              data-testid="send-verification-email-btn"
              variant="ghostPrimary"
              onClick={() => sendVerifyEmail(true)}
              isLoading={isSubmitting}
            >
              {t("page.verify-email.resend")}
            </Button>
            <Button variant="ghostSecondary" onClick={() => logout()}>
              {t("page.verify-email.go-back")}
            </Button>
          </Stack>
        </Stack>
      )}
    </AuthenticationPage>
  );
}

function sentVerificationEmailStorageKey(userId: string) {
  return "sent-verification-email-" + userId;
}

enum VerifyEmailStatus {
  NoSession,
  Verified,
  VerifiedDifferentDevice,
  Expired,
  NotYetVerified,
}

// I'm sorry for this.
async function getEmailVerifiedStatus({
  token,
  queryParamEmail,
  getAccessTokenPayload,
}: {
  token: string | null;
  queryParamEmail: string | null;
  getAccessTokenPayload: () => Promise<AccessTokenPayload | undefined>;
}) {
  const sessionExists = await Session.doesSessionExist();

  // Step 1. Check if the user is already verified and we don't need to validate the token
  if (token) {
    if (sessionExists) {
      const accessTokenPayload = await getAccessTokenPayload();
      if (queryParamEmail && accessTokenPayload && isLoggedInWith(queryParamEmail, accessTokenPayload)) {
        if (await isEmailVerified().then((x) => x.isVerified)) {
          return VerifyEmailStatus.Verified;
        }
      }
    }
  } else {
    if (!sessionExists) {
      return VerifyEmailStatus.NoSession;
    }

    if (await isEmailVerified().then((x) => x.isVerified)) {
      return VerifyEmailStatus.Verified;
    }
  }

  // Step 2. Check if the verify email token is correct
  const emailVerifyResponse = await verifyEmail();

  if (emailVerifyResponse.status === "OK") {
    if (!sessionExists) {
      return VerifyEmailStatus.VerifiedDifferentDevice;
    }

    const accessTokenPayload = await getAccessTokenPayload();
    if (queryParamEmail && accessTokenPayload && isLoggedInWith(queryParamEmail, accessTokenPayload)) {
      return VerifyEmailStatus.Verified;
    } else {
      return VerifyEmailStatus.VerifiedDifferentDevice;
    }
  }

  if (emailVerifyResponse.status === "EMAIL_VERIFICATION_INVALID_TOKEN_ERROR") {
    // This can happen if the verification code is expired or invalid.
    // We should ask the user to retry
    if (token) {
      if (sessionExists) {
        const accessTokenPayload = await getAccessTokenPayload();

        // If logged in with same user
        // We don't want to show an error if they're already verified
        if (queryParamEmail && accessTokenPayload?.emails[0] === queryParamEmail) {
          if (accessTokenPayload["st-ev"]?.v || (await isEmailVerified().then((x) => x.isVerified))) {
            return VerifyEmailStatus.Verified;
          }
        }

        return VerifyEmailStatus.Expired;
      }

      return VerifyEmailStatus.Expired;
    } else if (!sessionExists) {
      return VerifyEmailStatus.NoSession;
    } else {
      return VerifyEmailStatus.NotYetVerified;
    }
  }

  throw new Error("Unexpected email verification response");
}

function isLoggedInWith(email: string, accessTokenPayload: AccessTokenPayload) {
  return accessTokenPayload && email && accessTokenPayload.emails[0] === email;
}
